import { getProductDeliveryAvailablity } from "api/category/getProductDeliveryAvailablity";
import { proximityProductApi } from "api/category/proximityProductApi";
import { ICompactProduct } from "api/interfaces/get-page-details/common/CompactProduct.type";
import CardStyle from "component/category/product-grid-card/productGridCard.module.scss";
import LoaderSkeleton from "component/loader-skeleton/LoaderSkeleton";
import matchMediaConst from "constant/matchmedia";
import { useCustomScreenSize } from "hoc/useScreenSize";
import { RootState } from "interfaces/generics.type";
import { IVehcileDefaultValues } from "interfaces/session/IVehicle";
import dynamic from "next/dynamic";
import PropTypes from "prop-types";
import { memo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isMerchandiseCategory } from "util/product/apparel";
import { LAYOUT } from "../../../constant/layout-constant";
import { sendGA4ProductListViewEvent } from "../../../util/enhanced-ecommerce";
import styles from "./product-list.module.scss";

const ProductGridCard = dynamic(() => import(/* webpackChunkName: "product-grid-card" */ "component/category/product-grid-card/ProductGridCard"), {
  loading: () => <LoaderSkeleton height={400} width={350} count={1} gridViewNew classNameContainer="d-flex flex-wrap" />,
});
const ProductDetailCard = dynamic(() => import(/* webpackChunkName: "product-detail-card" */ "component/category/product-detail-card/ProductDetailCard"), {
  loading: () => <LoaderSkeleton height={200} count={1} />,
});
const B2BProductGridCard = dynamic(() => import(/* webpackChunkName: "b2b-product-grid-card" */ "component/category/product-grid-card/B2BProductGridCard"), {
  loading: () => <LoaderSkeleton height={400} width={350} count={1} gridViewNew classNameContainer="d-flex flex-wrap" />,
});

const propTypes = {
  products: PropTypes.array.isRequired,
};

interface IProps {
  products: ICompactProduct[];
  defaultVehicle: IVehcileDefaultValues;
}

function ProductList(props: IProps) {
  const {
    products, defaultVehicle, fitmentOptionsDetails,
  } = props;
  const { urlInformation } = useSelector((store: RootState) => store.utils);
  const isMerchandiseCat = isMerchandiseCategory(urlInformation);

  const isMobile = useCustomScreenSize({
    maxWidth: matchMediaConst.XS_MAX,
  });

  const layout = useSelector((state: RootState) => state.utils.layout);
  const { currentPageType } = useSelector((state: RootState) => state.utils);
  const { postalCode, hasRefreshed } = useSelector((state: RootState) => state.shoppingCart);
  const { businessCustomer } = useSelector((store: RootState) => store.authenticationState);
  const dispatch = useDispatch();
  const [proximityData, setProximityData] = useState([]);

  useEffect(() => {
    if (hasRefreshed) {
      getProductDeliveryAvailablity(products.map((_) => _.sku), postalCode)
        .then((res) => {
          dispatch({ type: "SET_PRODUCT_DELIVERY_METHOD_DETAILS", data: res.data.productDeliveryMethodDetails || [] });
        });
    }
  }, [hasRefreshed, postalCode, products]);

  useEffect(() => {
    sendGA4ProductListViewEvent(products, currentPageType || "NA");
  }, [products]);

  const skus = (products || []).map((product) => product.sku);

  useEffect(() => {
    if (products && products.length > 0 && businessCustomer) {
      proximityProductApi(skus).then((res) => {
        setProximityData(res.data);
      });
    }
  // call proximity api every time product list changes
  }, [businessCustomer, skus.join("")]);

  const PropsToPass = {
    style: styles.containerCompact,
    defaultVehicle,
  };

  if (!layout) {
    if (isMobile) {
      return (
        <LoaderSkeleton
          height={400}
          className={CardStyle.productContainer}
          gridView
          count={products?.length || 9}
        />
      );
    }

    return <LoaderSkeleton height={200} count={products?.length || 10} />;
  }

  if (products && products.length > 0) {
    return (
      <div
        className={styles.product_list}
        data-testid="product-list"
        data-clarity-unmask="true"
      >

        {(layout === LAYOUT.COMPACT || isMobile || isMerchandiseCat)
          && (
            <div className={`${styles.grid_container} ${products.length <= 2 ? "justify-content-around" : ""}`}>

              {businessCustomer && products.map((eachProduct: any) => (
                <B2BProductGridCard
                  {...PropsToPass
                  }
                  key={eachProduct.sku}
                  product={eachProduct}
                  fitmentOptionsDetails={fitmentOptionsDetails}
                  proximityData={proximityData}
                />
              ))}
              {!businessCustomer && products.map((eachProduct: any) => (
                <ProductGridCard
                  {...PropsToPass}
                  key={eachProduct.sku}
                  product={eachProduct}
                  fitmentOptionsDetails={fitmentOptionsDetails}
                />
              ))}

            </div>
          )}
        {(layout === LAYOUT.DETAILED && !isMobile && !isMerchandiseCat) && (
          <div className={styles.detail_container}>
            {products.map((eachProduct, index) => (

              <div
                key={index}
                name={`product-anchor-${eachProduct.canonicalUrl && eachProduct.canonicalUrl.substring(1)}`}
                className="m-b-10"
                id={`product-card-${eachProduct.sku}`}
              >
                <ProductDetailCard
                  {...PropsToPass}
                  key={eachProduct.sku}
                  product={eachProduct}
                  fitmentOptionsDetails={fitmentOptionsDetails}
                />

              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
  return <span />;
}

ProductList.propTypes = propTypes;

export default memo(ProductList);
